import { gql } from '@apollo/client';

/* ── stock research reports ─────────────────────────────────── */

/**
 * Generate a research report for one or more stocks.
 *
 * Runs the analysis agents and writes the report, which takes minutes — the
 * mutation returns as soon as the work is queued, so `success` means accepted,
 * not finished. The report appears by polling `getAllStockResearchReports`.
 * `overwrite` replaces the current report for that archetype rather than
 * stacking a second one; the superseded version stays readable through
 * `includeHistory`.
 */
export const TRIGGER_STOCK_REPORT = gql`
  mutation TriggerStockReport(
    $securitySymbols: [String!]!
    $archetype: String
    $overwrite: Boolean
    $generateReport: Boolean
    $workers: Int
  ) {
    triggerStockAnalysisReport(
      securitySymbols: $securitySymbols
      archetype: $archetype
      overwrite: $overwrite
      generateReport: $generateReport
      workers: $workers
    ) {
      success
      message
    }
  }
`;

/** Mark a generated report as reviewed. Answers with a bare boolean. */
export const VERIFY_STOCK_REPORT = gql`
  mutation VerifyStockReport(
    $securitySymbol: String!
    $archetype: String!
    $verified: Boolean!
  ) {
    verifyStockReport(
      securitySymbol: $securitySymbol
      archetype: $archetype
      verified: $verified
    )
  }
`;

/**
 * Send an analyst's correction back into the report.
 *
 * `patchApplied` says the agent edited the research; `softFeedbackAppended`
 * says it could not, and recorded the note against the stock instead. `mode`
 * names which path it took, and `error` is populated in place of both when the
 * agent refused — so all four have to be read before reporting an outcome.
 */
export const APPLY_STOCK_FEEDBACK = gql`
  mutation ApplyStockFeedback(
    $securitySymbol: String!
    $feedbackText: String!
    $archetype: String = "GENERAL"
    $regenerateReport: Boolean = true
  ) {
    applyStockResearchFeedback(
      securitySymbol: $securitySymbol
      feedbackText: $feedbackText
      archetype: $archetype
      regenerateReport: $regenerateReport
    ) {
      securitySymbol
      archetype
      patchApplied
      mode
      softFeedbackAppended
      reportUrl
      agentResponse
      error
    }
  }
`;
